/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 * 
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */

let LastID = 0

/**
 * A timer utility which is linked to the render loop. This is a drop-in replacement for setInterval and setTimeout.
 */
export default new class Timer {

    /** Stores pending tasks */
    tasks = []

    /** Debug: This is incremented every time a task is executed */
    tasksRunCount = 0

    /** Constructor */
    constructor() {

        // Create backup timer. This will allow executing the timer tasks even when the renderer is stopped.
        this.backupTimer = window.setInterval(e => this.onBackupTimer(), 250)

    }

    /** Must be called by the render function once every frame. */
    update() {

        // Store info
        this.isRunningTasks = true

        // Go through each task
        let now = Date.now()
        for (let i = 0 ; i < this.tasks.length ; i++) {

            // Get task
            let task = this.tasks[i]

            // Check if it should be run
            if (task.nextRun > now)
                continue

            // Run it
            try {
                this.tasksRunCount += 1
                task.callback()
            } catch (err) {
                console.error(`[Timer] Uncaught error:`, err)
            }

            // Update timer info
            task.nextRun = now + task.delay
            task.iterations += 1

            // Check if task should be removed now
            if (task.iterations >= task.maxIterations)
                this.tasks.splice(i--, 1)

        }

        // Store info
        this.lastUpdate = Date.now()
        this.isRunningTasks = false

    }

    /** @private Called every so often by the browser */
    onBackupTimer() {

        // Stop if running or if the update loop is working correctly
        if (this.isRunningTasks) return
        if (Date.now() - this.lastUpdate < 250) return

        // Update loop not working, run tasks manually now
        this.update()

    }

    /**
     * Run the specified function every X milliseconds.
     *
     * @param {Function} callback The function to execute
     * @param {number} delay The number of milliseconds between each iteration
     * @returns {number} The ID of this timer. You can pass to clearInterval() to stop it.
     */
    setInterval(callback, delay = 0) {

        // Add it
        let id = LastID++
        this.tasks.push({ id, callback, delay, nextRun: Date.now() + delay, iterations: 0, maxIterations: Number.POSITIVE_INFINITY })
        return id

    }

    /**
     * Run the specified function once after X milliseconds.
     * 
     * @param {Function} callback The function to execute
     * @param {number} delay The number of milliseconds between each iteration
     * @returns {number} The ID of this timer. You can pass to clearTimeout() to stop it.
     */
    setTimeout(callback, delay = 0) {

        // Add it
        let id = LastID++
        this.tasks.push({ id, callback, delay, nextRun: Date.now() + delay, iterations: 0, maxIterations: 1 })
        return id

    }

    /**
     * Clear the specified interval.
     * 
     * @param {number} id The timer ID to clear.
     */
    clearInterval(id) {

        // Find task index
        let idx = this.tasks.findIndex(t => t.id == id)
        if (idx == -1)
            return console.debug(`[Timer] Couldn't find task ID to remove: ${id}`)

        // Remove it
        this.tasks.splice(idx, 1)

    }

    /**
     * Clear the specified timeout.
     *
     * @param {number} id The timer ID to clear.
     */
    clearTimeout(id) {
        // Find task index
        const idx = this.tasks.findIndex(t => t.id == id)

        // Task has already executed and finished, so no need to clear it
        if (idx < 0) {
            return
        }

        // Remove it
        this.tasks.splice(idx, 1)
    }

}
